import { SharedApiV2 } from "@capstone/mock-api";
import { PartnerDto } from "mock-api/src/shared-v2-types";
import { SiteDTO } from "mock-api/src/shared-v2-types/site.types";
import { UserRole } from "src/app/core/b2c-users.service";
import {
  PartnerAvailabilityStatus,
  SiteAvailabilityStatus,
} from "src/app/core/constants";
import { PaginatedListQuery } from "src/app/core/request-v2-utils";
import { CapstonePartner } from "src/app/core/sites";
import {
  getApiDetailsDecorator,
  getEnumMember,
  getUpdatedPropertiesFilter,
  joinPath,
  parseUUID,
  UUID,
} from "src/utils";

type ApiPartners = SharedApiV2.PartnerSitesDto;
const api = getApiDetailsDecorator<ApiPartners>();

const urlSitePartMatch = /\/sites\/(?<id>_|[0-9]+)(?<childPath>\/.*|$)/i;

abstract class BasePartner {
  protected constructor(args: ClassProperties<BasePartner>) {
    this.customName = args.customName;
    this.defaultName = args.defaultName;
    this.key = args.key;
    this.status = args.status;
  }

  @api({ key: "displayName" }) public readonly customName: string | null;
  @api({ key: "name" }) public readonly defaultName: string;
  @api({ key: "partnerKey" }) public readonly key: UUID;
  @api() public readonly status: PartnerAvailabilityStatus;
}

export class Partner extends BasePartner {
  private constructor(args: Omit<ClassProperties<Partner>, "name">) {
    super(args);
    this.sites = args.sites;
    this.role = args.role;
    this.masterPartnerId = args.masterPartnerId;

    this.name = this.customName || this.defaultName;
  }

  public readonly name: string;
  @api() public readonly sites: SiteDTO[];
  @api() public readonly role: UserRole;
  @api() public readonly masterPartnerId: number;

  public static deserialize(data: ApiPartners): Partner {
    return new Partner({
      customName: data.displayName ?? null,
      defaultName: data.name,
      key: new UUID(data.partnerKey),
      status: getEnumMember(PartnerAvailabilityStatus, data.status),
      role: getEnumMember(UserRole, data.role),
      masterPartnerId: data.masterPartnerId,
      sites: data.sites.map((site) => ({
        ...site,
        status: getEnumMember(SiteAvailabilityStatus, site.status),
        role: getEnumMember(UserRole, site.role),
      })),
    });
  }

  public static deserializeList(value: ApiPartners[]): readonly Partner[] {
    return value.map((partner) => Partner.deserialize(partner));
  }

  /**
   * Parse the URL for partner info embedded in it.
   *
   * @param url - The URL to parse.
   */
  public static parseRouteInfo(url: string): {
    /**
     * The partner key from the URL, if present, or `"_"` if it's the "selected
     * partner" placeholder.
     */
    readonly partnerKey: UUID | "_" | null;
    /** The remaining part of the URL after the partner path. */
    readonly childPath: string;
  } {
    const groups: UrlMatchGroups = url.match(urlPartnerPartMatch)?.groups ?? {};
    return {
      partnerKey: groups.key === "_" ? "_" : parseUUID(groups.key),
      childPath: groups.childPath ?? url,
    };
  }

  /**
   * Replaces the partner portion of the provided URL and returns the new URL for
   * this partner.
   *
   * @param url - The URL to replace the partner path in.
   */
  public replaceRouted(url: string): string {
    return url.replace(
      urlPartnerPartMatch,
      `/partners/${this.key.toString()}$<childPath>`,
    );
  }

  public replaceSitedRouted(url: string, siteId: number): string {
    return this.replaceRouted(
      url.replace(urlSitePartMatch, `/sites/${siteId}$2`),
    );
  }

  public getRouteUrl(...childPaths: string[]): string {
    return getPartnerRouteUrl(this, ...childPaths);
  }

  public getApiUrl(...childPaths: string[]): string {
    return getPartnerApiUrl(this.key, ...childPaths);
  }
}

export class PartnerListItem extends BasePartner {
  private constructor(args: Omit<ClassProperties<PartnerListItem>, "name">) {
    super(args);
    this.name = args.customName || args.defaultName;
  }

  public readonly name: string;

  public static deserialize(data: PartnerDto): PartnerListItem {
    return new PartnerListItem({
      customName: data.displayName ?? null,
      defaultName: data.name,
      key: new UUID(data.partnerKey),
      status: getEnumMember(PartnerAvailabilityStatus, data.status),
    });
  }

  public static deserializeList(
    value: PartnerDto[],
  ): readonly PartnerListItem[] {
    return value.map((partner) => PartnerListItem.deserialize(partner));
  }
}

export interface PartnerAddArguments extends Omit<
  ClassProperties<PartnerAdd>,
  "defaultName" | "key"
> {
  partner: CapstonePartner;
}

export class PartnerAdd extends BasePartner {
  public constructor({ partner, ...args }: PartnerAddArguments) {
    super({ ...args, defaultName: partner.name, key: partner.key });

    this.masterPartnerId = partner.masterId;
  }

  private readonly masterPartnerId: number;

  public serialize(): SharedApiV2.PostPartnerDto {
    return {
      displayName: this.customName,
      masterPartnerId: this.masterPartnerId,
      name: this.defaultName,
      partnerKey: this.key.toString(),
      status: this.status,
    };
  }
}

export type PartnerUpdateArguments = Omit<
  ClassProperties<PartnerUpdate>,
  "defaultName" | "key"
>;

export class PartnerUpdate extends BasePartner {
  public constructor({ base, ...args }: PartnerUpdateArguments) {
    super({ ...base, ...args });
    this.base = base;

    this.filterUpdatedProperties = getUpdatedPropertiesFilter(this, base, args);
  }

  public readonly base: PartnerListItem;
  private readonly filterUpdatedProperties: FilterPartial<SharedApiV2.PatchPartnerDto>;

  public serialize(): Partial<SharedApiV2.PatchPartnerDto> {
    return this.filterUpdatedProperties({
      displayName: this.customName,
      status: this.status,
    });
  }
}

export function getPartnerRouteUrl(
  partner: Pick<Partner, "key">,
  ...childPaths: string[]
): string {
  return joinPath("/partners", String(partner.key), ...childPaths);
}

export function getPartnerApiUrl(
  partnerKey: Partner["key"],
  ...childPaths: string[]
): string {
  return joinPath("/partner", String(partnerKey), ...childPaths);
}

const urlPartnerPartMatch =
  /\/partners\/(?<key>_|[a-z0-9-]+)(?<childPath>\/.*|$)/i;
interface UrlMatchGroups {
  readonly key?: string;
  readonly childPath?: string;
}

export class PartnerListQueryModel extends PaginatedListQuery {
  public constructor(args: ClassProperties<PartnerListQueryModel>) {
    super(args);
  }
  public serialize(): SharedApiV2.GetPartnersQuery {
    return {
      ...this.serializeBase(),
    };
  }
}
